import type { PartnerLogoType } from '@app/components/UserPhoto2/load-partner-icon';
import {
  PARTNER_VERISK,
  PARTNER_COPART,
  PARTNER_CCC,
} from '@app/constants/partners';

export type PartnerCategory =
  | 'Available Integration'
  | 'System of Record'
  | 'NOT AVAILABLE';

export interface PartnerInfo {
  key: string;
  logo: PartnerLogoType;
  name: string;
  category: PartnerCategory;
}

// Marketing descriptions live in the `integrations` locale namespace under
// `partnerDescriptions.<key>` and are rendered via t() in integrations.tsx.
// CCC has no entry: it is NOT AVAILABLE (never rendered) and has no copy yet.
export const partnerInfo: Record<string, PartnerInfo> = {
  [PARTNER_CCC]: {
    key: PARTNER_CCC,
    logo: PARTNER_CCC,
    name: 'CCC',
    category: 'NOT AVAILABLE',
  },
  [PARTNER_COPART]: {
    key: PARTNER_COPART,
    logo: PARTNER_COPART,
    name: 'Copart',
    category: 'Available Integration',
  },
  duckcreek: {
    key: 'duckcreek',
    logo: 'duckcreek',
    name: 'Duck Creek',
    category: 'System of Record',
  },
  'guidewire-cc': {
    key: 'guidewire-cc',
    logo: 'guidewire',
    name: 'Guidewire ClaimCenter',
    category: 'System of Record',
  },
  'guidewire-in': {
    key: 'guidewire-in',
    logo: 'guidewire',
    name: 'Guidewire InsuranceNow',
    category: 'System of Record',
  },
  'insuuresoft-diamond': {
    key: 'insuuresoft-diamond',
    logo: 'insuresoft',
    name: 'Insuresoft Diamond',
    category: 'System of Record',
  },
  oneinc: {
    key: 'oneinc',
    logo: 'oneinc',
    name: 'OneInc',
    category: 'Available Integration',
  },
  'sapiens-cp': {
    key: 'sapiens-cp',
    logo: 'sapiens',
    name: 'Sapiens ClaimsPro',
    category: 'System of Record',
  },
  'sapiens-cs': {
    key: 'sapiens-cs',
    logo: 'sapiens',
    name: 'Sapiens CoreSuite',
    category: 'System of Record',
  },
  [PARTNER_VERISK]: {
    key: PARTNER_VERISK,
    logo: PARTNER_VERISK,
    name: 'Verisk',
    category: 'Available Integration',
  },
};

export const getPartnerInfo = (partner: string): PartnerInfo | null => {
  const partnerKey = partner?.toLowerCase() as PartnerLogoType;
  return partnerInfo[partnerKey] || null;
};

/**
 * Formats a raw partner name for display. Short all-uppercase names (<=3 chars)
 * are treated as acronyms and kept as-is (e.g. "CCC"). Longer names get
 * standard capitalization (first letter uppercase, rest lowercase).
 */
export const formatPartnerDisplayName = (name: string): string => {
  if (!name) return '';
  const info = getPartnerInfo(name);
  if (info) return info.name;
  const isAcronym = name.length <= 3 && name === name.toUpperCase();
  if (isAcronym) return name;
  return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
};
